import { Platform } from 'react-native';
import type ApiSettings from 'src/core/models/api/ApiSettings';
import compareVersions from 'src/core/utils/compareVersions';
import Config from 'src/core/utils/Config';
import Translations from 'src/core/utils/Translations';

/**
 * Store-update dialog, ported 1:1 from the Nou app (same styling + copy).
 */

/* Types */
const updateAvailableType = 'UpdateAvailable';
const mandatoryUpdateAvailableType = 'MandatoryUpdateAvailable';

/* Global constants */
const appStoreUrl = Config.appStoreUrl;
const playStoreUrl = Config.playStoreUrl;

/* Constants */
const AppUpdateDialogTypes = {
	UpdateAvailable: {
		dialogTitle: () => Translations.updateAvailable,
		dialogDescription: () => Translations.updateAvailableDescription,
		isCancellable: true,
	},
	MandatoryUpdateAvailable: {
		dialogTitle: () => Translations.updateAvailableMandatory,
		dialogDescription: () => Translations.updateAvailableMandatoryDescription,
		isCancellable: false,
	},
} as const;

/* Key */
type AppUpdateDialogKey = keyof typeof AppUpdateDialogTypes;

/**
 * The resolved update decision for the active platform, derived from the raw per-platform thresholds and the
 * current app version.
 */
export type ResolvedAppUpdate = {
	minimum_supported_app_version: string;
	recommended_app_version: string;
	should_mandatory_update_app: boolean;
	should_update_app: boolean;
};

/**
 * Picks the thresholds for the active platform and compares them against the installed version.
 */
export function resolveAppUpdate(settings: ApiSettings, currentVersion: string = Config.appVersion): ResolvedAppUpdate {
	const platform = Platform.OS === 'android' ? settings.app_update.android : settings.app_update.ios;
	const minVersion = platform.min_version;
	const recommendedVersion = platform.recommended_version;

	const shouldMandatoryUpdate = minVersion !== '' && compareVersions(currentVersion, minVersion) < 0;
	const shouldUpdate = recommendedVersion !== '' && compareVersions(currentVersion, recommendedVersion) < 0;

	return {
		minimum_supported_app_version: minVersion,
		recommended_app_version: recommendedVersion,
		should_mandatory_update_app: shouldMandatoryUpdate,
		should_update_app: shouldUpdate,
	};
}

/* Class */
class AppUpdateDialogType {
	private key: AppUpdateDialogKey;

	private constructor(key: AppUpdateDialogKey) {
		this.key = key;
	}

	/* Dialog types. */

	public static get updateAvailable(): AppUpdateDialogType {
		return new AppUpdateDialogType(updateAvailableType);
	}

	public static get mandatoryUpdateAvailable(): AppUpdateDialogType {
		return new AppUpdateDialogType(mandatoryUpdateAvailableType);
	}

	/* Methods. */

	public get type() {
		return this.key;
	}

	public get dialogTitle() {
		return AppUpdateDialogTypes[this.key].dialogTitle();
	}

	public get dialogDescription() {
		return AppUpdateDialogTypes[this.key].dialogDescription();
	}

	public get isCancellable() {
		return AppUpdateDialogTypes[this.key].isCancellable;
	}

	public get storeUrl(): string | null {
		if (Platform.OS === 'android') {
			return playStoreUrl;
		}
		if (Platform.OS === 'ios') {
			return appStoreUrl;
		}
		return null;
	}

	public get cancelButtonTitle() {
		return Translations.updateLater;
	}

	public get confirmButtonTitle() {
		return Translations.update;
	}

	public getDialogVersionDescription(update: ResolvedAppUpdate): string {
		return Translations.updateAvailableVersionText({
			currentVersion: Config.appVersion,
			newVersion: update.recommended_app_version,
		});
	}

	public getDialogVersionDescriptionBoldPart(update: ResolvedAppUpdate): string {
		return update.recommended_app_version;
	}
}

export default AppUpdateDialogType;
